import React from "react";
import Title from "../../ui/Title";

const TechnoSupport = () => {
  return (
    <div id="technology-supporting-caregiving" className="scroll-mt-24">
      <Title title="Technology supporting caregiving" className="mb-6" />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          Technology is a field that is advancing rapidly with each passing day.
          The scope of its application in caregiving is also broadening. With
          advancements in the field, a range of technology-based interventions
          can provide aid to caregivers. These include mobile applications with
          utilities spanning learning resources and courses for caregiving,
          medication management, tracking health records, financial management,
          tools to find and avail government benefits, and care team management
          (where care team implies both formal caregivers and family members who
          support in caregiving) and so on. The rapid progress made in the field
          of Artificial Intelligence (AI) has given rise to more opportunities
          for personalized support and automation of certain caregiving tasks.
          For example, developments of AI-based chatbots or assistants can
          answer caregiver’s questions and provide emotional support. At the
          same time, innovations in the Internet of Things (IoT), along with
          solutions with predictive analytics capabilities, help caregivers keep
          track of their loved one’s health from a distance and also be alerted
          when any fluctuations in their health are detected by the system.
          Emerging Virtual Reality (VR) solutions are being used for providing
          training to caregivers, especially for formal caregivers, simulating
          various situations.
        </p>

        <p>
          While these developments are promising, critical aspects of data
          privacy, ethics, and user-friendliness of emerging technology
          solutions also need consideration. This section provides an overview
          of some of the applications of technology as an aid for caregivers
          with examples.
        </p>

        <p>
          According to a{" "}
          <a
            href="https://pmc.ncbi.nlm.nih.gov/articles/PMC7019659/"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            2020 research paper
          </a>{" "}
          on technology and caregiving, the following are some applications of
          technology that support caregivers, along with examples of some of the
          existing interventions and products in each category:
        </p>

        <div>
          <h3 className="italic font-semibold mb-3">
            Artificial Intelligence (AI)
          </h3>

          <p>
            AI has seen rapid progress over the last few years, with
            advancements in natural language processing, personalization, and
            the ability to process large amounts of data and predict trends.
          </p>
        </div>

        <p>
          As a result, AI is increasingly being applied to support caregivers in
          a range of ways, including the following:
        </p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Improved access to information and decision-making: With greater
            data analysis capabilities, AI-powered solutions can help caregivers
            access important information and help them make more informed
            decisions.
          </li>
          <li>
            Anticipating care needs: AI and machine learning, when applied to
            technologies that support older adults, can anticipate and adjust to
            older adults’ abilities or needs, reducing demand on caregivers.
          </li>
          <li>
            Predictive health monitoring: Predictive analytics capabilities,
            when combined with wearable technology or IoT-based technologies,
            can predict potential health crises based on the data collected from
            a patient's vitals and movements or other behavioral data, which can
            help caregivers take action before a crisis.
          </li>
          <p>
            For example,{" "}
            <a
              href="https://www.carepredict.com/"
              className="text-blue underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              CarePredict
            </a>{" "}
            is a US-based tech company that has developed a wearable device to
            track daily activity and behavior (for example, changes in walking
            patterns) of older adults through sensors. Through its predictive
            capability, the technology alerts caregivers of changes in their
            individual behavior patterns or any fluctuations in important health
            parameters that may indicate a potential health issue. This helps
            monitor the situation and take action before a crisis. The
            technology is used at senior living facilities and at homes.
          </p>
          <li>
            Chatbots for information and emotional support: AI-powered chatbots
            are now available for caregivers to ask questions and receive timely
            and relevant information throughout their caregiving journey. These
            chatbots are also being used to provide emotional support to
            caregivers. Interventions like{" "}
            <a
              href="https://www.elizz.com/planning/caregiver-planning/how-a-smart-chatbot-with-personality-can-be-a-friend-for-family-caregivers/"
              className="text-blue underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              ELIZZ-bot
            </a>{" "}
            use techniques like cognitive behavioral therapy to support
            caregivers.
          </li>
          <li>
            Robots for assistance: Around the world, efforts are underway to
            develop robots capable of supporting older adults by assisting with
            daily tasks and offering companionship.
          </li>
          <div>
            <p>
              For example, the University of Hertfordshire in the UK is{" "}
              <a
                href="https://www.herts.ac.uk/research/impact-our-research/case-studies/robots-for-health-and-social-care"
                className="text-blue underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                developing care robots
              </a>{" "}
              for older adults and children with autism, and it is testing them
              by simulating real-world environments. The advancements in this
              field offer a promise to tackle problems such as the
              unavailability of caregivers and the burden on them. However,
              while many robots have been developed and sold already, existing
              interventions have several limitations, particularly in terms of
              autonomy and the range of real-life tasks they can perform. There
              are also concerns in terms of safety, ethics, and effectiveness.
            </p>

            <p>
              According to{" "}
              <a
                href="https://www.technologyreview.com/2023/01/09/1065135/japan-automating-eldercare-robots/"
                className="text-blue underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                a 2023 article
              </a>
              , in many instances, robots themselves “needed care” (such as
              moving them around, maintaining, cleaning, etc), which often
              created more work for the caregivers. While developments in the
              area of care robots are interesting, there is a long way to go and
              many questions need to be answered before care robots can provide
              tangible benefits to caregivers or older adults.
            </p>
          </div>
        </ul>

        <div>
          <h3 className="italic font-semibold mb-2">Virtual Reality (VR)</h3>

          <p>
            Virtual reality or VR is increasingly being used for training
            caregivers on caregiving tasks, handling emergencies, and performing
            medical or nursing tasks. VR can simulate various situations and
            help caregivers receive training in a simulated environment that
            resembles real-world situations.
          </p>
        </div>

        <p>
          VR is also being applied for empathy training, especially for formal
          caregivers. For example, the Canadian tech company KaleidoscopeXR has
          developed{" "}
          <a
            href="https://www.kaleidoscopexr.ca/home#:~:text=Our%20Solution,as%20they%20try%20to%20communicate."
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Caregiver VR
          </a>{" "}
          to provide empathy training for caregivers who support patients with
          dementia. The solution is designed for use in continuing care
          organizations, universities, and healthcare providers. Trainees go
          through VR simulations resembling real-life situations and symptoms of
          dementia, such as visual and auditory hallucinations, to understand
          what patients with dementia go through. They also learn to communicate
          effectively in different scenarios.
        </p>

        <div>
          <h3 className="italic font-semibold mb-2">Financial technologies</h3>

          <p>
            There are various financial technologies that allow remote
            monitoring of financial accounts and transactions. As older adults
            are susceptible to financial fraud, these tools help caregivers
            safeguard and monitor the financial accounts and transactions of
            older adults.
          </p>
        </div>

        <p>
          For example,{" "}
          <a
            href="https://www.eversafe.com/home-21/"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            EverSafe
          </a>{" "}
          is a product company based in the United States that provides tools to
          manage accounts and monitor financial activity. Among its many
          features, the product helps identify irregular financial activities
          and alerts caregivers or guardians.
        </p>

        <p>
          Certain applications also allow caregivers to identify government
          benefits they are eligible for, and support them in navigating the
          process of accessing these benefits. For example,{" "}
          <a
            href="https://www.joingivers.com/"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Givers
          </a>{" "}
          is a US-based company that provides a free-to-use tool through which
          caregivers can check their eligibility and get matched with government
          benefits for caregiving, based on each state’s policies. The tool
          reduces the need to navigate multiple government platforms and eases
          the process of identifying available benefits. The company helps
          caregivers with the enrollment process , and caregivers work with a
          licensed agency to document their caregiving work. In some states, the
          company itself serves as the licensing agency to ease the process.
        </p>

        <div>
          <h3 className="italic font-semibold mb-2">Assistive technologies</h3>

          <p>
            Assistive technologies that support care recipients with limited
            mobility or functional limitations like vision, hearing, and other
            abilities, can offer greater independence and autonomy for care
            recipients. While these technologies are not directly for
            caregivers, they can help substantially reduce the load on
            caregivers.
          </p>
        </div>

        <h3 className="italic font-semibold !mt-6 mb-3">
          Some major caregiver support platforms
        </h3>

        <div>
          <h4 className="italic mb-2">Medical information management</h4>

          <p>
            Many web and smartphone-based platforms help to keep track and
            manage all health records in one place. For example,{" "}
            <a
              href="https://carezone.com/"
              className="text-blue underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              CareZone
            </a>{" "}
            is a US-based company that offers a platform enabling users to keep
            track of all their health records, share them with healthcare
            providers, set reminders for taking medication, and access essential
            health services when necessary.
          </p>
        </div>

        <div>
          <h4 className="italic mb-2">
            E-learning and resources for caregivers
          </h4>

          <p>
            Many platforms provide resources, learning materials, and courses on
            different aspects like caregiving tasks, nutrition, information
            about health conditions, providing emotional support, taking care of
            one’s own well-being as a caregiver, and more. One such example is{" "}
            <a
              href="https://caregiversaathi.co.in/e-learning/home"
              className="text-blue underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Caregiver Saathi
            </a>
            , an Indian organization that offers various courses, resources and
            guidance from experts on various topics to support caregivers along
            with other curated services for caregivers.
          </p>
        </div>

        <div>
          <h4 className="italic mb-2">Care team management</h4>

          <p>
            A variety of platforms help manage and coordinate care teams (here,
            care team refers to formal caregivers as well as family members
            involved in caring for the patient). These platforms help facilitate
            easy communication with everyone in the care team, as well as
            coordinate and manage appointments, among other services. For
            example, the US-based platform{" "}
            <a
              href="https://caretree.me/"
              className="text-blue underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              CareTree
            </a>{" "}
            allows caregivers to coordinate appointments, send updates to family
            members and the rest of the care team, manage health records, and
            create a calendar for everyone in the care team to keep track of key
            events and appointments.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TechnoSupport;
